/**
 * Chrome built-in AI Prompt API integration.
 * Stage 1 of the chrome-ai embedding pipeline: image → text description.
 * Stage 2 (description text → 768-dim vector) is handled in app.ts via textExtractor.
 *
 * Requires Chrome 138+ with:
 *   chrome://flags/#optimization-guide-on-device-model  → Enabled BypassPerfRequirement
 *   chrome://flags/#prompt-api-for-gemini-nano           → Enabled
 */

// ── Type declarations for Chrome's built-in LanguageModel API ────────────────

export type LanguageModelAvailability = 'available' | 'downloadable' | 'downloading' | 'unavailable'

// Content items within a message
type LanguageModelContentItem =
  | { type: 'text'; value: string }
  | {
      type: 'image'
      value: ImageBitmap | Blob | HTMLCanvasElement | HTMLImageElement | HTMLVideoElement
    }

// Messages use role/content wrapper per the Prompt API spec
interface LanguageModelMessage {
  role: 'user' | 'assistant'
  content: LanguageModelContentItem[]
}

export interface LanguageModelSession {
  prompt(input: LanguageModelMessage[], options?: { signal?: AbortSignal }): Promise<string>
  destroy(): void
}

interface LanguageModelStatic {
  availability(options?: {
    expectedInputs?: Array<{ type: string; languages?: string[] }>
    expectedOutputs?: Array<{ type: string; languages?: string[] }>
  }): Promise<LanguageModelAvailability>
  create(options?: {
    expectedInputs?: Array<{ type: string; languages?: string[] }>
    expectedOutputs?: Array<{ type: string; languages?: string[] }>
    signal?: AbortSignal
    monitor?: (m: EventTarget) => void
  }): Promise<LanguageModelSession>
}

declare global {
  const LanguageModel: LanguageModelStatic | undefined
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const DEFAULT_DESCRIBE_PROMPT =
  'Describe this image for photo organization and clustering. ' +
  'Include: main subjects, setting or location, dominant colors, mood, ' +
  'lighting conditions, and any activity taking place. ' +
  'Be specific and concise (2-4 sentences).'

// Recycle the session after this many .prompt() calls to bound context growth.
const SESSION_RECYCLE_INTERVAL = 15

// ── Availability check ────────────────────────────────────────────────────────

export async function getChromeAIAvailability(): Promise<LanguageModelAvailability> {
  // Support both new global form and legacy window.ai.languageModel (pre-138)
  const api =
    (typeof LanguageModel !== 'undefined' ? LanguageModel : undefined) ??
    (window as unknown as { ai?: { languageModel?: LanguageModelStatic } }).ai?.languageModel
  if (!api) return 'unavailable'
  try {
    return await api.availability({
      expectedInputs: [{ type: 'image' }, { type: 'text', languages: ['en'] }],
      expectedOutputs: [{ type: 'text', languages: ['en'] }]
    })
  } catch {
    return 'unavailable'
  }
}

/**
 * Start Gemini Nano's one-time download and resolve once it is usable.
 * Chrome only starts the download from create() with user activation, so
 * call this synchronously inside a click handler, before any await.
 */
export function downloadChromeAI(onProgress: (fraction: number) => void): Promise<void> {
  return getAPI()
    .create({
      expectedInputs: [{ type: 'image' }, { type: 'text', languages: ['en'] }],
      expectedOutputs: [{ type: 'text', languages: ['en'] }],
      monitor: (m) => m.addEventListener('downloadprogress', (e) => onProgress((e as ProgressEvent).loaded))
    })
    .then((session) => session.destroy())
}

function getAPI(): LanguageModelStatic {
  const api =
    (typeof LanguageModel !== 'undefined' ? LanguageModel : undefined) ??
    (window as unknown as { ai?: { languageModel?: LanguageModelStatic } }).ai?.languageModel
  if (!api)
    throw new Error(
      'Chrome AI Prompt API not available. Enable chrome://flags/#prompt-api-for-gemini-nano in Chrome 138+.'
    )
  return api
}

// ── Session manager ───────────────────────────────────────────────────────────

/**
 * Manages a LanguageModel session with automatic recycling to bound context growth.
 * Each call to .describe() appends to the session's conversation history;
 * recycling every SESSION_RECYCLE_INTERVAL calls prevents unbounded memory use.
 */
export class ChromeAISessionManager {
  private slots: Array<{
    session: LanguageModelSession | null
    callCount: number
  }> = [
    { session: null, callCount: 0 },
    { session: null, callCount: 0 }
  ]

  async describe(
    image: ImageBitmap | Blob,
    prompt: string = DEFAULT_DESCRIBE_PROMPT,
    signal?: AbortSignal,
    slot = 0
  ): Promise<string> {
    const api = getAPI()
    const s = this.slots[slot % this.slots.length]

    if (s.session && s.callCount > 0 && s.callCount % SESSION_RECYCLE_INTERVAL === 0) {
      s.session.destroy()
      s.session = null
    }

    if (!s.session) {
      s.session = await api.create({
        expectedInputs: [{ type: 'image' }, { type: 'text', languages: ['en'] }],
        expectedOutputs: [{ type: 'text', languages: ['en'] }],
        signal
      })
    }

    // Prompt API requires an array of role/content messages
    const description = await s.session.prompt(
      [
        {
          role: 'user',
          content: [
            { type: 'image', value: image },
            { type: 'text', value: prompt }
          ]
        }
      ],
      { signal }
    )

    s.callCount++
    return description.trim()
  }

  destroy(): void {
    for (const s of this.slots) {
      s.session?.destroy()
      s.session = null
      s.callCount = 0
    }
  }
}
